import { useEffect, useRef, useState } from 'react';

import { reviewGroupFor } from '../utils/bulkLotNdcReviewGroups';

export const MOVED_ROW_HIGHLIGHT_MS = 1800;

const NO_ROW_IDS = new Set();

/**
 * POM-6534 — the rows whose grid group changed since the last render, for a brief highlight.
 *
 * Read from the classified rows rather than from the handlers that move them, so an edit, an undo,
 * a refused commit and a successful one are all caught the same way. The first render sets the
 * baseline and flags nothing.
 *
 * The timer lives in a ref, not in the effect's cleanup: the rows can be rebuilt again before it
 * fires without anything moving, and a cleanup would cancel the reset and leave the highlight on.
 */
const useRecentlyMovedRowIds = (reviewRows) => {
  const previousGroupById = useRef(null);
  const clearTimer = useRef(null);
  const [recentlyMovedIds, setRecentlyMovedIds] = useState(NO_ROW_IDS);

  useEffect(() => {
    const groupById = new Map(
      reviewRows.map((row) => [row.item.requestLineItemId, reviewGroupFor(row.status)])
    );
    const previous = previousGroupById.current;
    previousGroupById.current = groupById;
    if (!previous) return;

    const movedIds = [...groupById.entries()]
      .filter(([id, group]) => previous.has(id) && previous.get(id) !== group)
      .map(([id]) => id);
    if (movedIds.length === 0) return;

    setRecentlyMovedIds(new Set(movedIds));
    clearTimeout(clearTimer.current);
    clearTimer.current = setTimeout(() => setRecentlyMovedIds(NO_ROW_IDS), MOVED_ROW_HIGHLIGHT_MS);
  }, [reviewRows]);

  useEffect(() => () => clearTimeout(clearTimer.current), []);

  return recentlyMovedIds;
};

export default useRecentlyMovedRowIds;
